import { Node, mergeAttributes } from "@tiptap/core"
import type { Node as ProseMirrorNode } from "@tiptap/pm/model"
import { NodeViewContent, NodeViewWrapper, ReactNodeViewRenderer, type NodeViewProps } from "@tiptap/react"
import { Clock, X } from "lucide-react"
import { useState } from "react"
import { CameraPickButton, WithCameraClear } from "@/components/cameras/camera-pick-controls"
import { CameraPickerDialog } from "@/components/cameras/camera-picker-dialog"
import { useTranslation } from "@/hooks/use-translation"
import { useCameraPresetsContext } from "./camera-presets-context"
import { DEFAULT_TIMECODE } from "./serialize"

const TIMECODE_RANGE_RE = /(\d+)\s*-\s*(\d+)\s*sec/

/** Следующий блок продолжает шкалу: старт = конец последнего сабшота. */
function nextTimecode(doc: ProseMirrorNode): string {
  let maxEnd = 0
  doc.descendants((node) => {
    if (node.type.name === "promptBlock") {
      const m = String(node.attrs.timecode ?? "").match(TIMECODE_RANGE_RE)
      if (m) maxEnd = Math.max(maxEnd, Number(m[2]))
    }
    return true
  })
  return `${maxEnd}-${maxEnd + 1} sec`
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    promptBlock: {
      /** Обернуть текущий блок в посекундный блок промпта. */
      wrapInPromptBlock: () => ReturnType
      /** Снять обёртку блока. */
      liftPromptBlock: () => ReturnType
    }
  }
}

function PromptBlockView({ node, updateAttributes, editor, getPos }: NodeViewProps) {
  const { t } = useTranslation("shot")
  const timecode = String(node.attrs.timecode ?? DEFAULT_TIMECODE)
  const cameraPresetIdRaw = node.attrs.cameraPresetId != null ? Number(node.attrs.cameraPresetId) : null
  const cameraPresetId = cameraPresetIdRaw != null && Number.isFinite(cameraPresetIdRaw) ? cameraPresetIdRaw : null
  const { projectId, presets: cameraPresets, setPresets } = useCameraPresetsContext()
  const [cameraPickerOpen, setCameraPickerOpen] = useState(false)
  const selectedCamera = cameraPresetId != null ? (cameraPresets.find((p) => p.id === cameraPresetId) ?? null) : null

  const remove = () => {
    if (typeof getPos !== "function") return
    const pos = getPos()
    if (pos == null) return
    const node = editor.state.doc.nodeAt(pos)
    if (!node) return
    const from = pos + 1
    const to = pos + node.nodeSize - 1
    editor.chain().focus().setTextSelection({ from, to }).liftPromptBlock().run()
  }

  return (
    <NodeViewWrapper className="pe-block">
      <div className="pe-block__header" contentEditable={false}>
        <Clock className="h-3 w-3 shrink-0 text-ui-accent-text/80" />
        <input
          type="text"
          value={timecode}
          onChange={(e) => updateAttributes({ timecode: e.target.value })}
          className="pe-block__tc"
          placeholder="0-1 sec"
          aria-label={t("promptEditor.blockTimecode")}
        />
        <div className="pe-block__header-right">
          {projectId != null ? (
            <WithCameraClear
              showClear={cameraPresetId != null}
              clearTitle={t("promptEditor.clearSegmentCamera")}
              onClear={() => updateAttributes({ cameraPresetId: null })}
            >
              <CameraPickButton
                label={selectedCamera ? selectedCamera.name : t("promptEditor.segmentCamera")}
                title={t("promptEditor.pickSegmentCamera")}
                reserveClearSpace={cameraPresetId != null}
                onClick={() => setCameraPickerOpen(true)}
              />
            </WithCameraClear>
          ) : null}
          <button
            type="button"
            onClick={remove}
            className="pe-block__remove"
            title={t("promptEditor.removeBlock")}
            aria-label={t("promptEditor.removeBlock")}
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      </div>
      <NodeViewContent className="pe-block__body" />
      {projectId != null ? (
        <CameraPickerDialog
          open={cameraPickerOpen}
          onOpenChange={setCameraPickerOpen}
          projectId={projectId}
          presets={cameraPresets}
          selectedId={cameraPresetId}
          onSelect={(preset) => updateAttributes({ cameraPresetId: preset.id })}
          onPresetsLoaded={setPresets}
          nested
        />
      ) : null}
    </NodeViewWrapper>
  )
}

export const PromptBlock = Node.create({
  name: "promptBlock",
  group: "block",
  content: "paragraph+",
  defining: true,

  addAttributes() {
    return {
      timecode: {
        default: DEFAULT_TIMECODE,
        parseHTML: (el) => el.getAttribute("data-timecode") ?? DEFAULT_TIMECODE,
        renderHTML: (attrs) => ({ "data-timecode": String(attrs.timecode ?? DEFAULT_TIMECODE) }),
      },
      cameraPresetId: {
        default: null,
        parseHTML: (el) => {
          const raw = el.getAttribute("data-camera-preset-id")
          if (raw == null || raw === "") return null
          const n = Number(raw)
          return Number.isFinite(n) ? n : null
        },
        renderHTML: (attrs) => {
          const n = attrs.cameraPresetId == null ? null : Number(attrs.cameraPresetId)
          return n != null && Number.isFinite(n) ? { "data-camera-preset-id": String(n) } : {}
        },
      },
    }
  },

  parseHTML() {
    return [{ tag: "div[data-prompt-block]" }]
  },

  renderHTML({ HTMLAttributes }) {
    return ["div", mergeAttributes(HTMLAttributes, { "data-prompt-block": "" }), 0]
  },

  addNodeView() {
    return ReactNodeViewRenderer(PromptBlockView)
  },

  addCommands() {
    return {
      wrapInPromptBlock:
        () =>
        ({ state, commands }) =>
          commands.wrapIn(this.name, { timecode: nextTimecode(state.doc) }),
      liftPromptBlock:
        () =>
        ({ commands }) =>
          commands.lift(this.name),
    }
  },
})
